import Member from 'iam/models/Member'
import { PrimeUser } from 'iam/UserStore'
import { collection } from 'infra/collection'
import { BigMoneyPayoutsAdminAPI } from './API'
import {
  TransactionType,
  serializeTransactionType,
} from 'payouts/TransactionType'
import {
  UserTransactionReason,
  serializeUserTransactionReason,
} from 'payouts/UserTransactionReason'
import {
  AipCursorPaged,
  CursorPaged,
  CursorPageQuery,
  Id,
} from 'types/definitions'
import {
  PendingTransactionResource,
  DraftPayoutResource,
} from './dtos/drafting'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import PendingTransaction from './PendingTransaction'
import { Subject } from 'rxjs'
import { NotFound } from 'fejl'
import { DraftPayout } from './DraftPayout'
import { ApprovedTransaction } from './ApprovedTransaction'
import { GetCurrentUser } from 'iam/SessionStore'
import { CancellationToken } from 'infra/cancellation'
import { DateRange } from 'infra/FilterControls/DateRange/DateRangeFilter'
import { TransactionFlag, serializeTransactionFlag } from '../TransactionFlag'
import {
  TransactionSourceType,
  serializeTransactionSourceType,
} from '../TransactionSourceType'

/**
 * Manages logic, data and API interactions for reviewing payouts.
 */
export class PayoutReviewStore {
  /**
   * Used to notify of added pending transactions.
   * @private
   */
  private readonly _pendingTransactionAdded = new Subject<PendingTransaction>()

  /**
   * Used to notify of removed pending transactions.
   * @private
   */
  private readonly _pendingTransactionRemoved =
    new Subject<PendingTransaction>()

  /**
   * Used to notify of a payout having been submitted.
   *
   * @private
   */
  private readonly _payoutSubmitted = new Subject<DraftPayout>()

  /**
   * Emits a value whenever a pending transaction is added.
   */
  readonly pendingTransactionAdded$ =
    this._pendingTransactionAdded.asObservable()

  /**
   * Emits a value whenever a pending transaction is removed.
   */
  readonly pendingTransactionRemoved$ =
    this._pendingTransactionRemoved.asObservable()

  /**
   * Emits a value whenever a payout has been submitted.
   */
  readonly payoutSubmitted$ = this._payoutSubmitted.asObservable()

  /**
   * Tracks pending transactions.
   */
  readonly pendingTransactions = collection<
    PendingTransactionResource,
    PendingTransaction
  >({
    create: () => new PendingTransaction(this.primeUser, this.primeWorkspace),
  })

  /**
   * Tracks draft payouts.
   */
  readonly draftPayouts = collection<DraftPayoutResource, DraftPayout>({
    create: () => new DraftPayout(this.primeUser, this.primeWorkspace),
  })

  /**
   * Constructor.
   */
  constructor(
    private api: BigMoneyPayoutsAdminAPI,
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
    private getCurrentUser: GetCurrentUser,
  ) {}

  /**
   * Fetches the current draft payout.
   */
  async fetchCurrentDraftPayout(): Promise<DraftPayout> {
    // When there is no current draft, it is likely because one is being
    // created after having submitted one, so we can wait for it.
    const response = await NotFound.retry(
      () => this.api.draftPayouts.getCurrent(),
      { tries: 5, minTimeout: 200, maxTimeout: 1_000 },
    )
    return this.draftPayouts.set(response)
  }

  /**
   * Fetches draft payout by ID.
   */
  async fetchDraftPayout(id: Id): Promise<DraftPayout> {
    const response = await this.api.draftPayouts.get(Id.assert(id))
    return this.draftPayouts.set(response)
  }

  /**
   * Lists pending transactions.
   *
   * @param query
   * @param paging
   * @param cancellationToken
   */
  async listPendingTransactions(
    query: ListPendingTransactionsQuery,
    paging: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<PendingTransaction>> {
    const response = await this.api.pendingTransactions.list(
      {
        page_size: paging.limit,
        page_token: paging.after,
        order_by: 'date_created desc',
        recipient_user_id: query.recipientUserId,
        workspace_ids: query.workspaceIds?.length
          ? query.workspaceIds?.join(',')
          : undefined,
        date_transacted_from: query.dateTransacted?.from ?? undefined,
        date_transacted_to: query.dateTransacted?.to ?? undefined,
        date_created_from: query.dateCreated?.from ?? undefined,
        date_created_to: query.dateCreated?.to ?? undefined,
        search_text: query.searchText,
        flags: query.flags?.map(serializeTransactionFlag).join(','),
        transaction_source: query.sources
          ?.map(serializeTransactionSourceType)
          .join(','),
      },
      cancellationToken,
    )

    const paged = AipCursorPaged.map(response, (r) =>
      this.pendingTransactions.set(r),
    )
    return CursorPaged.fromAipCursorPaged(paged)
  }

  /**
   * Creates a new user transaction.
   *
   * @param command
   */
  async createUserTransaction(command: CreateUserTransaction): Promise<void> {
    // Create the transaction
    const { id } = await this.api.transactions.createUserTransaction({
      recipient_user_id: Id.assert(command.recipient.user.publicId),
      workspace_id: Id.assert(command.recipient.workspace.id),
      type: serializeTransactionType(command.type),
      reason: serializeUserTransactionReason(command.reason),
      amount: command.amount,
      memo: command.memo,
    })

    // Retrieve the transaction in the background and add it to the
    // pending list.
    this._retrieveCreatedTransaction(id).then()
  }

  /**
   * Cancels a user transaction.
   *
   * @param command
   */
  async cancelUserTransaction(command: CancelUserTransaction): Promise<void> {
    await this.api.transactions.cancelUserTransaction(
      Id.assert(command.transaction.id),
    )
    this.pendingTransactions.remove(command.transaction)
    this._pendingTransactionRemoved.next(command.transaction)
  }

  /**
   * Approves transactions for payout.
   */
  async approveTransactions(command: ApproveTransactions): Promise<void> {
    const { draft, transactions } = command
    const approvedBy = this.getCurrentUser()
    const approvedAt = new Date()
    const response = await this.api.draftPayouts.approveTransactions(
      Id.assert(draft.id),
      {
        transaction_ids: transactions.map((t) => Id.assert(t.id)),
      },
    )

    draft.approveTransactions(
      transactions,
      approvedBy,
      approvedAt,
      response.etag,
    )
  }

  /**
   * Removes approved transactions from a draft payout.
   */
  async removeTransactions(command: RemoveTransactions): Promise<void> {
    const { draft, transactions } = command
    const response = await this.api.draftPayouts.removeTransactions(
      Id.assert(draft.id),
      {
        transaction_ids: transactions.map((t) => Id.assert(t.id)),
      },
    )

    draft.removeTransactions(transactions, response.etag)
  }

  /**
   * Submits the payout.
   */
  async submitPayout(command: SubmitPayout): Promise<void> {
    const { draft } = command
    await this.api.draftPayouts.submitPayout(Id.assert(draft.id), {
      etag: draft.etag,
    })
    this._payoutSubmitted.next(draft)
  }

  /**
   * Checks if the payout has completed by asking the server.
   */
  async checkPayoutCompletion(id: Id): Promise<boolean> {
    // We know that if this gets called, then the payout does exist.
    const response = await NotFound.retry(
      () => this.api.payouts.get(Id.assert(id)),
      {
        tries: 5,
      },
    )
    return response.completed
  }

  /**
   * Retrieves a recently created transaction and pushes it to consumers.
   *
   * @param id
   * @private
   */
  private async _retrieveCreatedTransaction(id: string) {
    // Retrieve the transaction that was just created.
    // We do this in a retry since the read side might be eventually
    // consistent.
    const transaction = await NotFound.retry(
      () => this.api.transactions.get(id),
      { tries: 5 },
    )
    const pendingTransaction = this.pendingTransactions.set(transaction)
    this._pendingTransactionAdded.next(pendingTransaction)
  }
}

/**
 * Lists pending transactions.
 */
export interface ListPendingTransactionsQuery {
  recipientUserId?: Id
  dateCreated?: DateRange | null
  dateTransacted?: DateRange | null
  flags?: TransactionFlag[] | null
  sources?: TransactionSourceType[] | null
  workspaceIds?: Id[] | null
  searchText?: string
}

/**
 * Command for creating a user transaction
 */
export interface CreateUserTransaction {
  /**
   * The workspace member to create the transaction for.
   */
  recipient: Member
  /**
   * The transaction memo.
   */
  memo: string
  /**
   * The transaction amount.
   */
  amount: number
  /**
   * The transaction type.
   */
  type: TransactionType
  /**
   * The reason for the transaction.
   */
  reason: UserTransactionReason
}

/**
 * Command for cancelling a user transaction.
 */
export interface CancelUserTransaction {
  /**
   * The transaction being canceled.
   */
  transaction: PendingTransaction
}

/**
 * Command for approving transactions for payout.
 */
export interface ApproveTransactions {
  draft: DraftPayout
  transactions: PendingTransaction[]
}

/**
 * Command for removing approved transactions from a draft payout.
 */
export interface RemoveTransactions {
  draft: DraftPayout
  transactions: ApprovedTransaction[]
}

/**
 * Command for submitting a draft payout.
 */
export interface SubmitPayout {
  draft: DraftPayout
}
